'use client';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from '@/components/ui/use-toast';
import { passwordCollection } from '@/lib/actions/collection/action';
import Cookies from 'js-cookie';
import { IStoreInfoData } from '@/types/general';
import { Loader2 } from 'lucide-react';

type FormPassword = {
  password: string;
};

type ProtectionFormProps = {
  data: IStoreInfoData;
};

const PasswordProtectionScheme = Yup.object().shape({
  password: Yup.string().required('Password is a required'),
});

export default function ProtectionForm(data: ProtectionFormProps) {
  const handleOnSubmit = async (
    values: FormPassword,
    { setErrors }: FormikHelpers<FormPassword>
  ) => {
    try {
      const host = window.location.host;
      const isDevelopment = process.env.REACT_APP_ENV === 'development';
      const res = await passwordCollection(values.password, host);
      if (res?.success) {
        Cookies.set('_is_valid_access', String(res.data.data), {
          expires: 7,
          secure: !isDevelopment && window.location.protocol === 'https:',
          sameSite: 'strict',
        });
        toast({ title: res.data?.message, variant: 'success', duration: 1500 });
        setTimeout(() => {
          window.location.reload();
        }, 1600);
      } else {
        setErrors({ password: res?.data?.message });
      }
    } catch {
      toast({ title: 'Error', description: 'Something went wrong.', variant: 'destructive' });
    }
  };
  return (
    <div className='flex h-screen flex-col items-center justify-center p-4'>
      <div className='w-full max-w-md rounded-md border border-slate-300 p-4 shadow-lg'>
        <h1 className='theme_font_heading mb-4 text-center text-2xl'>Password Protection</h1>
        <p className='text-base'>
          {data.data.site_protection_message || 'This site is password protected.'}
        </p>
        <Formik
          key='passwordProtectCollection'
          initialValues={{ password: '' }}
          validationSchema={PasswordProtectionScheme}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, handleSubmit, handleChange, handleBlur, touched, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <div className='flex w-full flex-col'>
                <Input
                  name='password'
                  type='password'
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder='Password'
                  classNameWrapper='w-full mt-4'
                  required
                />
                {errors.password && touched.password && (
                  <span className='mt-1 text-red-500'>{errors.password}</span>
                )}
              </div>
              <Button className='theme_button mt-4 w-full text-sm' type='submit'>
                {isSubmitting ? <Loader2 className='h-4 w-4' /> : 'Submit'}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
